// SPDX-License-Identifier: GPL-3.0-or-later
// Where each table element is, for the feedback events. Built once from the table data.
// The position is the centre of the element in table units (x right, y down the table, z up).
// A position object is shared by every event of its element: a reader must not change it.

import type { TableSpec, Vec3 } from "../../shared/contracts.ts";

/** Look up the position of an element by name. Unknown name: undefined. */
export type PositionOf = (element: string) => Vec3 | undefined;

interface Centered {
  readonly name: string;
  readonly center: { readonly x: number; readonly y: number };
}

interface Placed {
  readonly name: string;
  readonly position: Vec3;
}

interface Outlined {
  readonly name: string;
  readonly dragPoints: readonly { readonly vertex: Vec3 }[];
}

/** The centre of an outline: the mean of its points. An empty outline has no centre. */
function centroid(points: readonly { readonly vertex: Vec3 }[]): Vec3 | null {
  if (points.length === 0) return null;
  let x = 0;
  let y = 0;
  let z = 0;
  for (const p of points) {
    x += p.vertex.x;
    y += p.vertex.y;
    z += p.vertex.z;
  }
  const n = points.length;
  return { x: x / n, y: y / n, z: z / n };
}

function addCentered(map: Map<string, Vec3>, items: readonly Centered[]): void {
  for (const item of items) map.set(item.name, { x: item.center.x, y: item.center.y, z: 0 });
}

function addPlaced(map: Map<string, Vec3>, items: readonly Placed[]): void {
  for (const item of items) map.set(item.name, item.position);
}

function addOutlined(map: Map<string, Vec3>, items: readonly Outlined[]): void {
  for (const item of items) {
    const c = centroid(item.dragPoints);
    if (c) map.set(item.name, c);
  }
}

/** The position of every named element of the table. */
export function buildPositions(table: TableSpec): ReadonlyMap<string, Vec3> {
  const map = new Map<string, Vec3>();
  addOutlined(map, [...table.walls, ...table.rubbers, ...table.ramps]);
  addCentered(map, [
    ...table.flippers,
    ...table.bumpers,
    ...table.kickers,
    ...table.triggers,
    ...table.gates,
    ...table.spinners,
    ...table.lights,
    table.plunger,
  ]);
  addPlaced(map, [...table.targets, ...table.decorations]);
  return map;
}

/** A lookup function for a table. */
export function positionLookup(table: TableSpec): PositionOf {
  const map = buildPositions(table);
  return (element) => map.get(element);
}
